/**
 * BoardErrorRetry — 네트워크 에러 시 재시도 UI
 *
 * 로드 실패 시 에러 메시지와 재시도 버튼을 표시.
 * 가볍고 자연스러운 인라인 에러 표시.
 */
import React from 'react';
import { Trans, t } from '@lingui/macro';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';

interface Props {
  message?: string;
  onRetry: () => void;
  compact?: boolean;
}

const BoardErrorRetry: React.FC<Props> = ({
  message = t`Failed to load data`,
  onRetry,
  compact = false,
}) => {
  if (compact) {
    return (
      <div className="flex items-center justify-center gap-2 py-3">
        <AlertTriangle className="size-3.5 text-destructive" />
        <span className="text-xs text-muted-foreground">{message}</span>
        <button
          onClick={onRetry}
          className="text-xs font-medium text-primary hover:text-primary/80 transition-colors cursor-pointer"
        >
          <Trans>Retry</Trans>
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 h-full px-6">
      <div className="size-16 rounded-2xl bg-destructive/10 flex items-center justify-center">
        <AlertTriangle className="size-7 text-destructive" strokeWidth={1.5} />
      </div>
      <div className="text-center">
        <p className="text-base font-semibold text-foreground">
          <Trans>Load failed</Trans>
        </p>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-[260px]">
          {message}
        </p>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={onRetry}
        className="mt-1"
      >
        <RefreshCw className="size-4 mr-1.5" />
        <Trans>Retry</Trans>
      </Button>
    </div>
  );
};

export default BoardErrorRetry;
